// ============================================================================
// TERMO REVOGACAO CLIENTE ENGINE — Motor do Termo de Revogação de Mandato
// Layout: sidebar 360px + painel A4 central + painel direito (padrão
// idêntico a DeclarationEngine.jsx / ProcurationEngine.jsx / ContractEngine.jsx)
//
// Escopo desta v1 (decisão registrada em 15/08/2026): documento é um
// template FECHADO — sem editor de cláusulas inline (ver nota equivalente
// em TermoRenunciaAdvogadoEngine.jsx, irmão deste motor).
// ============================================================================

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Download, CheckCircle, Trash2, Plus } from "lucide-react";
import {
  DocumentShell,
  ConfigSection,
} from "../../components/layout/DocumentShell";
import {
  getDownloadURL,
  getStorage,
  ref as storageRef,
  uploadBytes,
} from "firebase/storage";
import { db, auth } from "../../shared/api/firebase/config";
import { FIREBASE_CONFIG } from "../../config/Config";
import {
  loadRevogacaoTemplate,
  subscribeRevogacaoTemplate,
  updateRevogacaoDocumentStructure,
  findClientWorkspace,
  findExistingRevogacao,
  saveRevogacao,
  publishRevogacao,
} from "../../services/TermoRevogacaoClienteRepository.js";
import { CrmClientPickerModal } from "../../components/CrmClientPickerModal";
import { CadernoSelector } from "../../components/ui/CadernoSelector";
import { useOfficeData } from "../settings/useOfficeData";
import {
  TERMO_REVOGACAO_STRUCTURE,
  buildRevogacaoPlaceholderVars,
  resolveRevogacaoText,
} from "./TermoRevogacaoClienteData";
import { buildSignatureLine, CLIENT_TYPES } from "../../config/ClientSchema";
import {
  DocumentHeader,
  DocumentFooter,
} from "../../components/layout/DocumentLetterhead";
import { SaveCancelBar } from "../../components/ui/SaveCancelBar";
import "../../styles/legal-print.css";
import { fetchWithWarmup } from "../../utils/backendWarmup";
import { toTitleCasePt } from "../../utils/formatters";

const appId = FIREBASE_CONFIG.projectId;
const storage = getStorage();
const PUBLISHING_API_BASE_URL = String(
  import.meta?.env?.VITE_BACKEND_URL || "https://rda-backend-thuq.onrender.com",
)
  .trim()
  .replace(/\/+$/, "");
const AUTOSAVE_DELAY_MS = 8000;
const DRAFT_KEY = "rda_termo_revogacao_draft_v1";

const labelBaseStyle = "text-[10px] font-bold text-gray-500 uppercase tracking-widest";
const inputBaseStyle =
  "w-full px-2.5 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-[12px] font-medium text-gray-800 outline-none transition-all focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059]";

const escapeHtmlAttr = (v) =>
  String(v || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

export function TermoRevogacaoClienteFactory() {
  const { data: officeData } = useOfficeData();

  const [openSection, setOpenSection] = useState("client");
  const [showImport, setShowImport] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [cloudSyncState, setCloudSyncState] = useState("idle");
  const [error, setError] = useState("");
  const [toastMsg, setToastMsg] = useState("");
  const [isModified, setIsModified] = useState(false);
  const [cloudSyncMeta, setCloudSyncMeta] = useState({
    updatedAt: null,
    updatedBy: null,
    source: null,
    error: null,
  });
  const [dialogState, setDialogState] = useState({
    open: false,
    title: "",
    message: "",
    mode: "confirm",
    onConfirm: null,
    onCancel: null,
  });

  // MULTI-PARTE (Art. 391-392, CONSTITUICAO_REGRAS.SH): `parties` é um array
  // de referências LEVES (não o objeto do cliente inteiro) — mesmo shape e
  // mesmo padrão de Contrato/Procuração/Declaração (`form.parties`/
  // `formData.parties`), copiado, não importado cruzado (Art. 350). Sempre
  // começa com 1 slot vazio (nunca array vazio) — é isso que faz o card
  // aparecer mesmo sem cliente importado, igual aos outros 4 documentos.
  // `qualificationData` guarda o objeto do cliente hidratado (usado por
  // `buildCanonicalClientQualification`); `id` é um id LOCAL do slot
  // (nunca o id do cliente no CRM — esse fica em `clientId`).
  const makeEmptyParty = () => ({
    id: `party_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    clientId: null,
    name: "",
    cpf: "",
    role: "REVOGANTE",
    qualificationData: null,
  });
  const [parties, setParties] = useState(() => [makeEmptyParty()]);
  const [importTargetPartyId, setImportTargetPartyId] = useState(null);
  const [processoRef, setProcessoRef] = useState("");
  const [objetoMandato, setObjetoMandato] = useState("");
  const [editingPublicationMeta, setEditingPublicationMeta] = useState(null);
  const [cadernoId, setCadernoId] = useState("");
  const [cadernoTitle, setCadernoTitle] = useState("");
  const [structure, setStructure] = useState(TERMO_REVOGACAO_STRUCTURE);

  const draftPromptedRef = useRef(false);
  const centerPanelRef = useRef(null);

  // Mesma regra de junção pt-BR do Contrato (joinPartiesPt): usada aqui só
  // para nomes de exibição (título do documento, nome de arquivo) — a
  // junção da QUALIFICAÇÃO completa (com vírgulas internas de cada uma)
  // vive em TermoRevogacaoClienteData.js, não aqui.
  const joinNamesPt = (arr) => {
    const a = (Array.isArray(arr) ? arr : []).filter((x) => String(x || "").trim() !== "");
    if (a.length <= 1) return a[0] || "";
    if (a.length === 2) return `${a[0]} e ${a[1]}`;
    return `${a.slice(0, -1).join("; ")}; e ${a[a.length - 1]}`;
  };

  // Fonte única para nome/CPF de exibição — cobre TODOS os tipos de
  // cliente (física, jurídica, ANIMAL, menor), não só física. Bug
  // corrigido em 27/08/2026 (Art. 393): o card do 2º REVOGANTE (animal)
  // ficava com "Nome" em branco porque só `qualification.physical.name`
  // era checado antes do fallback a `c?.name` de topo — para ANIMAL o
  // campo correto é `qualification.animal.name`. Usada tanto para gravar
  // o valor no import quanto para EXIBIR no card (lida direto de
  // `qualificationData`, nunca de um campo `name`/`cpf` separado que possa
  // dessincronizar).
  const resolveClientDisplayName = (c) =>
    String(
      c?.qualification?.physical?.name ||
        c?.qualification?.legal?.companyName ||
        c?.qualification?.animal?.name ||
        c?.qualification?.minor?.name ||
        c?.name ||
        "",
    ).trim();
  const resolveClientDisplayCpf = (c) =>
    String(
      c?.qualification?.physical?.cpf || c?.qualification?.legal?.cnpj || "",
    ).trim();

  const partiesList = Array.isArray(parties) ? parties : [];
  // `clientsList` = só os slots com cliente de fato importado (hidratado
  // via qualificationData) — slots vazios (sempre existe pelo menos 1,
  // para o card aparecer) não entram na qualificação/assinatura/título.
  const clientsList = partiesList
    .map((p) => p?.qualificationData)
    .filter(Boolean);
  const primaryClient = clientsList[0] || null;

  const placeholderVars = useMemo(
    () =>
      buildRevogacaoPlaceholderVars({
        clientsData: clientsList,
        officeData,
        processoRef,
        objetoMandato,
      }),
    [clientsList, officeData, processoRef, objetoMandato],
  );

  const clauseBlocks = useMemo(() => structure.filter((b) => b.type === "clause"), [structure]);

  useEffect(() => {
    const applyTemplate = (tplData) => {
      if (tplData && Array.isArray(tplData.structure)) {
        setStructure(tplData.structure);
      }
    };
    loadRevogacaoTemplate()
      .then(applyTemplate)
      .catch((err) =>
        console.warn(
          "[TermoRevogacaoEngine] Erro ao carregar template, usando fallback local:",
          err?.message,
        ),
      );
    const unsubscribe = subscribeRevogacaoTemplate(applyTemplate);
    return unsubscribe;
  }, []);

  // Nome/id "principais" — usados em título do documento, nome de arquivo
  // e vínculo de Caderno/portal do cliente. `clientId` é sempre do PRIMEIRO
  // cliente da lista (mesmo padrão do Contrato: form.parties[0] é a "main
  // party" para esses fins, ver ContractEngine.jsx). Com múltiplos
  // REVOGANTES, o nome exibido é a junção de todos (joinNamesPt).
  const clientName = joinNamesPt(clientsList.map(resolveClientDisplayName));
  const clientId = String(primaryClient?.id || "").trim();

  const showToast = useCallback((msg) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(""), 3500);
  }, []);

  useEffect(() => {
    if (!isModified) return;
    const docId = String(editingPublicationMeta?.documentId || "").trim();
    if (!docId) return;
    setCloudSyncState("saving");
    const timer = setTimeout(async () => {
      try {
        const actorName = String(
          auth?.currentUser?.displayName || auth?.currentUser?.email || "Operador",
        ).trim();
        const now = new Date().toISOString();
        await updateRevogacaoDocumentStructure(
          docId,
          structure,
          processoRef,
          objetoMandato,
          actorName,
        );
        setIsModified(false);
        setCloudSyncState("saved");
        setCloudSyncMeta({ updatedAt: now, updatedBy: actorName, source: "cloud_autosave", error: null });
      } catch (e) {
        setCloudSyncState("error");
        setCloudSyncMeta((prev) => ({ ...prev, error: e?.message || "Erro no autosave" }));
      }
    }, AUTOSAVE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [isModified, structure, processoRef, objetoMandato, editingPublicationMeta]);

  useEffect(() => {
    if (draftPromptedRef.current) return;
    try {
      const saved = localStorage.getItem(DRAFT_KEY);
      if (!saved) return;
      const draft = JSON.parse(saved);
      if (!Array.isArray(draft?.parties) || draft.parties.length === 0) return;
      draftPromptedRef.current = true;
      setDialogState({
        open: true,
        title: "Rascunho Encontrado",
        message:
          'Detectamos um Termo de Revogação que estava sendo editado anteriormente.\n\nDeseja restaurar este rascunho para continuar o trabalho?',
        mode: "confirm",
        onConfirm: () => {
          try {
            setParties(draft.parties);
            if (draft.processoRef) setProcessoRef(draft.processoRef);
            if (draft.objetoMandato) setObjetoMandato(draft.objetoMandato);
            if (draft.cadernoId) setCadernoId(draft.cadernoId);
            if (draft.cadernoTitle) setCadernoTitle(draft.cadernoTitle);
            setIsModified(true);
          } catch {
            /* ignore */
          }
        },
        onCancel: () => {
          try {
            localStorage.removeItem(DRAFT_KEY);
          } catch {
            /* ignore */
          }
        },
      });
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (!isModified || clientsList.length === 0) return;
    try {
      localStorage.setItem(
        DRAFT_KEY,
        JSON.stringify({ parties: partiesList, processoRef, objetoMandato, cadernoId, cadernoTitle }),
      );
    } catch {
      /* ignore */
    }
  }, [isModified, clientsList, partiesList, processoRef, objetoMandato, cadernoId, cadernoTitle]);

  const addParty = useCallback(() => {
    setParties((prev) => [...(Array.isArray(prev) ? prev : []), makeEmptyParty()]);
    setIsModified(true);
  }, []);

  const removeParty = useCallback((id) => {
    setParties((prev) => {
      const arr = Array.isArray(prev) ? prev : [];
      if (arr.length <= 1) return arr;
      const next = arr.filter((p) => p?.id !== id);
      const stillHasClient = next.some((p) => p?.qualificationData);
      if (!stillHasClient) {
        setCadernoId("");
        setCadernoTitle("");
        setIsModified(false);
      } else {
        setIsModified(true);
      }
      return next;
    });
  }, []);

  // Ao importar cliente no PRIMEIRO slot pela 1ª vez, busca caderno/
  // documento existente (mesmo comportamento de sempre). Importar num
  // slot 2º/3º em diante (co-REVOGANTE, ex.: 2º animal da mesma tutora),
  // ou reimportar um slot que não é o primeiro, não repete essa busca.
  const handleImportClient = useCallback(async (picked) => {
    if (!picked) return;
    const pickedId = String(picked?.id || picked?.clientId || "").trim();
    const targetId = importTargetPartyId || partiesList?.[0]?.id;
    const isFirstSlot = !partiesList?.[0] || partiesList[0].id === targetId;
    const name = resolveClientDisplayName(picked);
    const cpf = resolveClientDisplayCpf(picked);
    setParties((prev) => {
      const arr = Array.isArray(prev) ? prev : [makeEmptyParty()];
      const idx = arr.findIndex((p) => p.id === targetId);
      const safeIdx = idx >= 0 ? idx : 0;
      const next = [...arr];
      next[safeIdx] = {
        ...next[safeIdx],
        clientId: pickedId,
        name,
        cpf,
        qualificationData: picked,
      };
      return next;
    });
    setShowImport(false);
    setImportTargetPartyId(null);
    setIsModified(true);
    setCloudSyncState("idle");
    if (!isFirstSlot) return;
    if (!pickedId) {
      setEditingPublicationMeta(null);
      setCadernoId("");
      setCadernoTitle("");
      return;
    }
    try {
      const firstWs = await findClientWorkspace(pickedId);
      if (firstWs) {
        setCadernoId(firstWs.id);
        setCadernoTitle(String(firstWs.data()?.title || "").trim());
      } else {
        setCadernoId("");
        setCadernoTitle("");
      }
    } catch (_wsErr) {
      setCadernoId("");
      setCadernoTitle("");
    }
    try {
      const latest = await findExistingRevogacao(pickedId);
      if (latest) {
        setEditingPublicationMeta({
          documentId: latest.id,
          publicationVersion: Number(latest.data?.publicationVersion || 0),
        });
      } else {
        setEditingPublicationMeta(null);
      }
    } catch (_err) {
      setEditingPublicationMeta(null);
    }
  }, [importTargetPartyId, partiesList]);

  const handleSaveHtml = useCallback(async () => {
    if (clientsList.length === 0) {
      setError("Importe ao menos um cliente (parte REVOGANTE) do CRM antes de salvar.");
      return;
    }
    if (!cadernoId) {
      setError("Selecione o Caderno de Autos antes de salvar. O Termo precisa estar vinculado a um processo.");
      return;
    }
    if (isSaving) return;
    setIsSaving(true);
    setError("");
    setCloudSyncState("saving");
    try {
      const actorName = String(
        auth?.currentUser?.displayName || auth?.currentUser?.email || "Operador",
      ).trim();
      const title = `Termo de Revogação de Mandato — ${toTitleCasePt(clientName)}`;
      const existingId = String(editingPublicationMeta?.documentId || "").trim();
      const now = new Date().toISOString();
      const payload = {
        type: "MANDATE_TERMINATION",
        subtype: "REVOGACAO_CLIENTE",
        title,
        name: title,
        clientId,
        clientIds: clientsList.map((c) => String(c?.id || "").trim()).filter(Boolean),
        clientVisible: false,
        status: "PUBLISHED",
        structure: JSON.parse(JSON.stringify(structure)),
        processoRef,
        objetoMandato,
        category: "FORMAL_DOCUMENT",
        classification: { category: "termo_revogacao" },
        module: "mandate_termination",
        requiresSignature: true,
        requiresConsent: false,
        consentStatus: "not_required",
        publication: {
          source: "termo_revogacao_engine",
          format: "html",
          publishedAtIso: now,
          publishedBy: actorName,
          storageMode: "html_archive",
        },
        cadernoId: cadernoId || null,
        cadernoTitle: cadernoTitle || null,
        createdBy: auth?.currentUser?.email || null,
        updatedBy: auth?.currentUser?.email || null,
      };

      const docId = await saveRevogacao(payload, clientId, existingId);

      setEditingPublicationMeta({ documentId: docId, publicationVersion: 0 });
      setIsModified(false);
      setCloudSyncState("saved");
      setCloudSyncMeta({ updatedAt: now, updatedBy: actorName, source: "html_save", error: null });
      showToast("Termo de Revogação salvo no prontuário. NÃO visível ao cliente ainda. Publique o PDF para liberar.");
    } catch (err) {
      console.error("[TermoRevogacaoEngine] Save HTML failed:", err);
      setError(`Erro ao salvar: ${err?.message || "Desconhecido"}`);
      setCloudSyncState("error");
      setCloudSyncMeta((prev) => ({ ...prev, error: err?.message || "Erro desconhecido" }));
    } finally {
      setIsSaving(false);
    }
  }, [
    clientsList,
    structure,
    processoRef,
    objetoMandato,
    clientId,
    clientName,
    editingPublicationMeta,
    isSaving,
    showToast,
    cadernoId,
    cadernoTitle,
  ]);

  const handlePublishPdf = useCallback(async () => {
    if (!editingPublicationMeta?.documentId) {
      setError("Salve o HTML primeiro antes de publicar o PDF.");
      return;
    }
    if (isPublishing) return;
    setIsPublishing(true);
    setError("");
    setCloudSyncState("saving");
    try {
      if (!isModified) {
        try {
          const freshData = await loadRevogacaoTemplate();
          if (freshData?.structure && Array.isArray(freshData.structure)) {
            setStructure(freshData.structure);
            await new Promise((resolve) => {
              requestAnimationFrame(() => {
                requestAnimationFrame(() => setTimeout(resolve, 800));
              });
            });
          }
        } catch (_freshErr) {
          /* non-blocking */
        }
      }

      const docId = String(editingPublicationMeta.documentId).trim();
      const safeCadernoId = String(cadernoId || "").trim();
      const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      const stem = `[${ts}]-Termo-Revogacao-Mandato-${clientName.replace(/\s+/g, "-").toUpperCase()}`;
      const fileName = `${stem}.pdf`;
      const storagePath = `artifacts/${appId}/public/documents/mandate_termination/${clientId}/${fileName}`;

      let fileUrl = "";
      let downloadUrl = "";

      try {
        const paperSource = (centerPanelRef.current || document).querySelector(".contract-print-area");
        const paperClone = paperSource ? paperSource.cloneNode(true) : null;

        const normalizeAbsoluteUrl = (raw) => {
          const value = String(raw || "").trim();
          if (!value) return "";
          try {
            return new URL(value, window.location.origin).href;
          } catch {
            return value;
          }
        };

        if (paperClone) {
          paperClone.querySelectorAll("[src]").forEach((node) => {
            try {
              const current = node.getAttribute("src");
              if (current) node.setAttribute("src", normalizeAbsoluteUrl(current));
            } catch {}
          });
        }

        const styleTags = Array.from(document.querySelectorAll("style"))
          .map((node) => `<style>${String(node?.textContent || "")}</style>`)
          .join("\n");
        const linkTags = Array.from(document.querySelectorAll('link[rel="stylesheet"]'))
          .map((node) => {
            const href = normalizeAbsoluteUrl(node?.getAttribute("href"));
            return href ? `<link rel="stylesheet" href="${escapeHtmlAttr(href)}" />` : "";
          })
          .filter(Boolean)
          .join("\n");

        const docTitle = `Termo de Revogação de Mandato — ${toTitleCasePt(clientName)}`;
        const bodyHtml =
          paperClone?.outerHTML ||
          `<div class="published-paper-fallback"><h1>${escapeHtmlAttr(docTitle)}</h1><p>Snapshot indisponível.</p></div>`;
        const actorLabel =
          String(auth?.currentUser?.displayName || auth?.currentUser?.email || "Operador").trim() || "Operador";

        const fullHtml = `<!doctype html>
<html lang="pt-BR">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${escapeHtmlAttr(docTitle)}</title>
  ${linkTags}
  ${styleTags}
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;700&display=swap">
  <style>
    html, body { background: #f3f4f6; margin: 0; padding: 0; }
    body { font-family: 'Roboto', sans-serif; -webkit-font-smoothing: antialiased; color: #20243B; }
    .published-shell { max-width: 1120px; margin: 0 auto; padding: 24px; }
    .publication-banner { margin-bottom: 16px; border: 1px solid #dbe2ea; background: #fff; border-radius: 16px; padding: 16px 18px; }
    .publication-banner h1 { margin: 0 0 8px 0; font-size: 18px; font-weight: 800; }
    .publication-banner p { margin: 4px 0; font-size: 12px; line-height: 1.6; }
    .no-print { display: none !important; }
    @media print {
      html, body { background: white !important; }
      .published-shell { max-width: none !important; margin: 0 !important; padding: 0 !important; }
      .publication-banner { display: none !important; }
    }
  </style>
</head>
<body>
  <div class="published-shell">
    <div class="publication-banner">
      <h1>${escapeHtmlAttr(docTitle)}</h1>
      <p>Documento publicado no sistema interno do Portal do Cliente em ${escapeHtmlAttr(new Date().toLocaleString("pt-BR"))} por ${escapeHtmlAttr(actorLabel)}.</p>
    </div>
    ${bodyHtml}
  </div>
</body>
</html>`;

        const resp = await fetchWithWarmup(
          `${PUBLISHING_API_BASE_URL}/drive/generate-pdf?t=${Date.now()}`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ html: fullHtml, fileName }),
          },
          {
            onWaking: () => showToast("⏳ Backend acordando... aguarde até 40s na primeira vez."),
            onRetry: (attempt, max) => showToast(`🔄 Tentativa ${attempt + 1}/${max}... aguarde.`),
          },
        );
        if (resp.ok) {
          const pdfBlob = await resp.blob();
          const sRef = storageRef(storage, storagePath);
          await uploadBytes(sRef, pdfBlob, { contentType: "application/pdf" });
          downloadUrl = await getDownloadURL(sRef);
          fileUrl = downloadUrl;
        }
      } catch (pdfErr) {
        console.warn("[TermoRevogacaoEngine] PDF via backend failed (non-blocking):", pdfErr?.message);
      }

      const currentVersion = Number(editingPublicationMeta?.publicationVersion || 0) + 1;
      const now = new Date().toISOString();
      const actorName = String(
        auth?.currentUser?.displayName || auth?.currentUser?.email || "Operador",
      ).trim();
      await publishRevogacao(docId, {
        status: "PUBLISHED",
        fileUrl,
        downloadUrl,
        storagePath: fileUrl ? storagePath : "",
        fileName,
        mimeType: "application/pdf",
        extension: "pdf",
        publicationVersion: currentVersion,
        clientVisible: true,
        cadernoId: safeCadernoId || null,
        cadernoTitle: cadernoTitle || null,
        publication: {
          source: "termo_revogacao_engine",
          format: "pdf",
          publishedAtIso: now,
          publishedBy: actorName,
          storageMode: fileUrl ? "pdf_archive" : "html_archive",
        },
        updatedBy: auth?.currentUser?.email || null,
      });

      setEditingPublicationMeta((prev) => ({
        ...prev,
        publicationVersion: currentVersion,
        cadernoId: safeCadernoId || null,
        cadernoTitle: cadernoTitle || null,
      }));
      setIsModified(false);
      setCloudSyncState("saved");
      setCloudSyncMeta({ updatedAt: now, updatedBy: actorName, source: "pdf_publish", error: null });
      showToast(
        fileUrl
          ? "✅ Termo de Revogação publicado em PDF — visível no portal do cliente!"
          : "⚠️ Publicado (HTML). Backend PDF indisponível — verifique o Render.",
      );
    } catch (err) {
      console.error("[TermoRevogacaoEngine] Publish PDF failed:", err);
      setError(`Erro ao publicar PDF: ${err?.message || "Desconhecido"}`);
      setCloudSyncState("error");
      setCloudSyncMeta((prev) => ({ ...prev, error: err?.message || "Erro" }));
    } finally {
      setIsPublishing(false);
    }
  }, [editingPublicationMeta, isPublishing, isModified, clientName, clientId, cadernoId, cadernoTitle, showToast]);

  const handlePrint = useCallback(() => {
    const previousTitle = document.title;
    const nextTitle = clientName ? `Termo Revogacao - ${clientName}` : "Termo de Revogacao de Mandato";
    const restoreTitle = () => {
      document.title = previousTitle;
      window.removeEventListener("afterprint", restoreTitle);
    };
    document.title = nextTitle;
    window.addEventListener("afterprint", restoreTitle);
    window.print();
    window.setTimeout(restoreTitle, 1000);
  }, [clientName]);

  const scrollToBlock = useCallback((blockId) => {
    const el = document.getElementById(`revogacao-block-${blockId}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  }, []);

  const renderBlock = (block, clauseIndex, { forPrint = false } = {}) => {
    if (block.type === "text") {
      const resolvedContent = resolveRevogacaoText(block.content || "", placeholderVars);
      return (
        <div key={block.id} id={block.id} className="clause-block mb-2" style={{ borderLeft: "none", paddingLeft: "0.5rem" }}>
          <h2 className="clause-heading preamble-heading relative">
            <span className="clause-heading-row w-full flex items-start flex-wrap gap-1">
              <span className="clause-title">PREÂMBULO</span>
            </span>
          </h2>
          <div className="contract-p clause-body" dangerouslySetInnerHTML={{ __html: resolvedContent }} />
        </div>
      );
    }

    const children = block.children || [];
    return (
      <div
        key={block.id}
        id={forPrint ? `block-${block.id}` : `revogacao-block-${block.id}`}
        className="clause-block relative"
        style={{ borderLeft: "none", paddingLeft: "0.5rem" }}
      >
        <h2 className="clause-heading relative">
          <span className="clause-heading-row w-full flex items-start flex-wrap gap-1">
            <span className="clause-number">Cláusula {clauseIndex}ª –</span>
            <span className="clause-title">{block.title}</span>
          </span>
        </h2>
        <div className="clause-body mt-2 space-y-1.5">
          {children.map((ch, idx) => (
            <div key={ch.id} className="sub-clause-item has-manual-number">
              <div className="clause-li-row" style={{ "--clause-num-col": "4.5ch" }}>
                <span
                  style={{
                    flexShrink: 0,
                    fontWeight: 700,
                    fontSize: "11pt",
                    whiteSpace: "nowrap",
                    color: "var(--text-primary,#374151)",
                  }}
                >
                  {clauseIndex}.{idx + 1}.
                </span>
                <span dangerouslySetInnerHTML={{ __html: resolveRevogacaoText(ch.content || "", placeholderVars) }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderQuickGuide = (forPrint = false) => (
    <div id={forPrint ? "guia-rapido-print" : "guia-rapido"} className={`quadro-resumo-section mb-8${forPrint ? " print-quick-nav-page" : ""}`}>
      <h2 className="text-center font-bold text-sm mb-4 uppercase relative">Guia Rápido do Documento</h2>
      <div className="quadro-resumo-grid">
        {clauseBlocks.map((block, idx) => (
          <a
            key={block.id}
            href={forPrint ? `#block-${block.id}` : `#revogacao-block-${block.id}`}
            onClick={
              forPrint
                ? undefined
                : (e) => {
                    e.preventDefault();
                    scrollToBlock(block.id);
                  }
            }
            className="quadro-resumo-card group relative"
          >
            <span className="quadro-resumo-icon" aria-hidden="true">
              <i className={`fas ${block.icon || "fa-file-alt"}`} aria-hidden="true" />
            </span>
            <div className="quadro-resumo-content">
              <h3 className="quadro-resumo-title-group">
                <span className="quadro-resumo-clause-number">Cláusula {idx + 1}ª</span>
                <span className="quadro-resumo-label" title={block.title}>{block.title}</span>
              </h3>
            </div>
          </a>
        ))}
      </div>
    </div>
  );

  // Espaçamento (mb-4/py-3/mb-2/mb-1) e vão de assinatura (1,6cm) são
  // canônicos e idênticos nos 5 documentos de fechamento (Contrato,
  // Procuração, Declaração, Termo de Renúncia, Termo de Revogação) — não
  // são valores arbitrários. Ver CONSTITUICAO_REGRAS.SH Art. 384-386 antes
  // de alterar.
  const renderClosing = () => (
    <div className="mb-4">
      <p className="text-justify leading-7 text-[11.5pt] text-gray-800 mb-4">
        Nessa condição, a presente revogação produz efeitos imediatos a partir da comunicação à parte <strong>MANDATÁRIA</strong>, cessando o mandato ora revogado nos termos do art. 682, inciso I, da Lei n. 10.406/2002 (Código Civil), sem prejuízo do direito da parte <strong>MANDATÁRIA</strong> aos honorários já devidos e aos honorários de sucumbência proporcionais ao trabalho efetivamente prestado até a presente data.
      </p>
      <p className="text-justify leading-7 text-[11.5pt] text-gray-800 mb-4">
        E, por corresponder à cristalina expressão da verdade e de sua livre vontade, a parte <strong>REVOGANTE</strong> firma o presente Termo.
      </p>

      <div className="rounded-xl border border-amber-200 bg-amber-50/80 px-4 py-3 text-[10.5pt] leading-6 text-amber-950 text-justify mb-4">
        <strong>
          Nota de Validade Híbrida (art. 654, § 1º, da Lei n. 10.406/2002 —
          Código Civil Brasileiro):
        </strong>{" "}
        Na hipótese de assinatura eletrônica remota, o local de emissão, a data
        e a hora oficiais do ato coincidem de forma inalterável com os dados de
        geolocalização, IP e Carimbo do Tempo registrados no relatório de
        auditoria da plataforma certificadora anexa, suprindo plenamente o
        preenchimento manual dos campos. Em caso de assinatura física
        presencial, inclusive no interior de estabelecimentos prisionais ou
        hospitalares, o local exato e a data deverão ser preenchidos de
        próprio punho pela parte <strong>REVOGANTE</strong> ou por seu
        patrono nos campos abaixo.
      </div>

      <div className="mb-4 rounded-xl border border-dashed border-stone-300 bg-white px-4 py-3">
        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-stone-500 mb-2">Local e Data de Emissão</p>
        <p className="text-[10.5pt] italic leading-6 text-stone-400 mb-1">
          (apenas para assinatura física presencial)
        </p>
        <p className="text-[11.5pt] leading-7 text-gray-800 break-words">
          <span className="inline-block min-w-[220px] border-b border-stone-400 align-baseline">&nbsp;</span>,{" "}
          <span className="inline-block min-w-[42px] text-center border-b border-stone-400 align-baseline">&nbsp;</span> de{" "}
          <span className="inline-block min-w-[170px] border-b border-stone-400 align-baseline">&nbsp;</span> de{" "}
          <span className="inline-flex min-w-[76px] border-b border-stone-400 align-baseline justify-start"><span className="pl-1">20</span></span>.
        </p>
      </div>
    </div>
  );

  // Um bloco de assinatura por REVOGANTE (mesmo padrão do Contrato: N
  // partes → N colunas de assinatura, ver ContractEngine.jsx
  // renderSignatureColumn). Cada animal co-outorgante é representado por
  // sua/seu responsável legal — buildSignatureLine já resolve isso e
  // preenche o subtítulo "(Assinando na qualidade de responsável legal
  // pelo animal não-humano X)" por cliente, então 2 animais da mesma
  // tutora geram 2 blocos com o mesmo nome/CPF mas subtítulos distintos.
  // Agrupa por ASSINANTE REAL (nome + CPF), não por "parte" (item do
  // array de clientes) — bug corrigido em 27/08/2026 (Art. 395): antes,
  // 1 responsável legal representando 2 animais co-clientes (mesmo nome,
  // mesmo CPF) virava 2 blocos de assinatura duplicados. Um humano assina
  // 1 vez só, mesmo representando várias partes; o subtítulo é combinado
  // ("...pelos animais não-humanos [PET_A] e [PET_B]") em vez de repetir o
  // bloco inteiro. `representedEntityName`/`representedEntityType` vêm de
  // `buildSignatureLine` (ClientSchema.js, campos aditivos do Art. 395).
  const renderSignature = () => {
    const list = clientsList.length > 0 ? clientsList : [null];
    const groups = [];
    list.forEach((c) => {
      const s = c ? buildSignatureLine(c) : null;
      const name = s?.nameLine || "[Revogante não informado]";
      const cpf = (s?.cpfLine || "CPF: [Não informado]").replace(/^CPF:\s*/i, "CPF ");
      const key = `${name}|${cpf}`;
      let group = groups.find((g) => g.key === key);
      if (!group) {
        group = { key, name, cpf, signatures: [] };
        groups.push(group);
      }
      group.signatures.push(s);
    });

    const buildGroupSubtitle = (signatures) => {
      const withEntity = signatures.filter((s) => s?.representedEntityName);
      if (withEntity.length === 0) return "";
      if (withEntity.length === 1)
        return String(withEntity[0].subtitle || "").trim();
      const sameType = withEntity.every(
        (s) => s.representedEntityType === withEntity[0].representedEntityType,
      );
      if (!sameType) {
        // Tipos diferentes no mesmo signatário (raro) — empilha as frases
        // originais em vez de tentar combinar gramaticalmente.
        return withEntity
          .map((s) => String(s.subtitle || "").trim())
          .filter(Boolean)
          .join(" ");
      }
      const names = joinNamesPt(withEntity.map((s) => s.representedEntityName));
      const type = withEntity[0].representedEntityType;
      if (type === CLIENT_TYPES.ANIMAL)
        return `(Assinando na qualidade de responsável legal pelos animais não-humanos ${names})`;
      if (type === CLIENT_TYPES.ESTATE)
        return `(Assinando na qualidade de inventariante dos Espólios de ${names})`;
      // LEGAL/MINOR: "representante legal de X e Y" já funciona
      // gramaticalmente sem pluralizar o substantivo.
      return `(Assinando na qualidade de representante legal de ${names})`;
    };

    return (
      <div className="flex flex-col items-center gap-6">
        {groups.map((g, idx) => {
          const sub = buildGroupSubtitle(g.signatures);
          const label = groups.length > 1 ? `REVOGANTE ${idx + 1}` : "REVOGANTE";
          return (
            <div key={g.key} className="w-full max-w-md text-center border-t border-black pt-2">
              <span className="font-bold">{g.name}</span>
              <br />
              <span className="text-[11px]">{g.cpf} — {label}</span>
              {sub ? (<><br /><span className="text-[11px]">{sub}</span></>) : null}
            </div>
          );
        })}
      </div>
    );
  };

  const rightPanelItems = clauseBlocks.map((block) => ({ id: block.id, label: String(block.title).toUpperCase(), draggable: false }));

  const centerPanelJsx = (
    <div ref={centerPanelRef} className="flex-1 overflow-auto relative p-4 md:p-8 bg-gray-100 min-w-0 min-h-0 print:p-0 print:overflow-visible print:bg-white">
      <div className="contract-print-area">
        <div className="screen-only-view p-8 bg-gray-100 flex justify-center">
          <div className="screen-paper">
            <div className="text-center mb-8 relative">
              <h1 className="contract-title">TERMO DE <span className="italic">REVOGAÇÃO DE MANDATO</span></h1>
            </div>
            <div className="document-content space-y-6">
              {renderQuickGuide(false)}
              {(() => {
                let clauseIdx = 0;
                return structure.map((block) => {
                  if (block.type === "clause") clauseIdx++;
                  return renderBlock(block, clauseIdx, { forPrint: false });
                });
              })()}
              <div className="avoid-page-break-inside">
                {renderClosing()}
                <div style={{ marginTop: "1.6cm" }} aria-hidden="true" />
                <div className="mb-8">{renderSignature()}</div>
              </div>
            </div>
          </div>
        </div>
        <div className="print-only-view">
          <div className="hybrid-visual-header-fixed"><DocumentHeader /></div>
          <div className="hybrid-visual-footer-fixed"><DocumentFooter /></div>
          <table className="print-layout-table">
            <thead className="hybrid-table-header-group"><tr><td><div className="spacer-header-ghost">&nbsp;</div></td></tr></thead>
            <tbody>
              <tr>
                <td>
                  <div className="document-content pt-4 pb-4">
                    <div className="text-center mb-8">
                      <h1 className="contract-title">TERMO DE <span className="italic">REVOGAÇÃO DE MANDATO</span></h1>
                    </div>
                    {renderQuickGuide(true)}
                    {(() => {
                      let clauseIdx = 0;
                      return structure.map((block) => {
                        if (block.type === "clause") clauseIdx++;
                        return renderBlock(block, clauseIdx, { forPrint: true });
                      });
                    })()}
                    <div className="avoid-page-break-inside">
                      {renderClosing()}
                      <div style={{ marginTop: "1.6cm" }} aria-hidden="true" />
                      <div className="mb-8">{renderSignature()}</div>
                    </div>
                    <div id="contrato-legal-pronto" data-status="ready" style={{ display: "none" }}></div>
                  </div>
                </td>
              </tr>
            </tbody>
            <tfoot className="hybrid-table-footer-group"><tr><td><div className="spacer-footer-ghost">&nbsp;</div></td></tr></tfoot>
          </table>
        </div>
      </div>
    </div>
  );

  const actionBarJsx = (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <button
          onClick={() => {
            if (clientsList.length === 0) {
              setError("Importe o cliente antes de salvar.");
              return;
            }
            setDialogState({
              open: true,
              title: "Salvar Termo (HTML)",
              message: "Esta ação irá salvar o Termo em HTML no prontuário interno. O documento NÃO será enviado ao portal do cliente. Deseja continuar?",
              mode: "confirm",
              onConfirm: handleSaveHtml,
            });
          }}
          className="px-2.5 py-1.5 rounded-lg font-semibold text-[11px] whitespace-nowrap border border-violet-200 bg-white text-violet-700 hover:bg-violet-50 transition-colors flex items-center justify-center gap-1.5 leading-none shadow-sm"
          type="button"
        >
          <i className="fas fa-save"></i> {isSaving ? "Salvando..." : "Salvar HTML"}
        </button>
        <button
          onClick={() => {
            if (!editingPublicationMeta?.documentId) {
              setDialogState({ open: true, title: "Salve primeiro", message: "Salve o HTML antes de publicar o PDF.", mode: "info" });
              return;
            }
            setDialogState({
              open: true,
              title: "Publicar PDF",
              message: "Esta ação irá publicar o Termo em PDF no Firebase Storage e torná-lo visível no portal do cliente. Deseja continuar?",
              mode: "confirm",
              onConfirm: handlePublishPdf,
            });
          }}
          className="px-2.5 py-1.5 rounded-lg font-semibold text-[11px] whitespace-nowrap border bg-[#20243B] text-white border-[#20243B] hover:brightness-95 transition-colors flex items-center justify-center gap-1.5 leading-none shadow-sm"
          type="button"
        >
          <i className="fas fa-cloud-upload-alt"></i> {isPublishing ? "Publicando..." : "Publicar PDF"}
        </button>
      </div>
      <div className="mt-2">
        <button
          onClick={handlePrint}
          className="w-full px-2.5 py-1.5 rounded-lg font-semibold text-[11px] whitespace-nowrap shadow-sm flex justify-center gap-1.5 transition-colors items-center no-print border bg-white text-gray-700 border-gray-200 hover:bg-gray-50 leading-none"
          type="button"
        >
          <i className="fas fa-print" /> PDF / Imprimir
        </button>
      </div>
    </>
  );

  return (
    <>
      <DocumentShell
        title="Motor de Termo de Revogação"
        tabs={[
          { id: "client", label: "Revogante / Mandatária" },
          { id: "processo", label: "Processo" },
        ]}
        activeTab={openSection}
        onTabChange={setOpenSection}
        actionBar={actionBarJsx}
        syncState={cloudSyncState}
        syncMeta={cloudSyncMeta}
        error={error}
        rightPanelTitle="Mapa do Termo"
        rightPanelItems={rightPanelItems}
        onRightPanelItemClick={(item) => scrollToBlock(item.id)}
        centerPanel={centerPanelJsx}
      >
        <>
          <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            <ConfigSection id="client" title="Da Parte REVOGANTE (Cliente)" icon="fa-user" openSection={openSection} setOpenSection={setOpenSection}>
              <div className="space-y-2.5">
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setImportTargetPartyId(partiesList?.[0]?.id || null);
                      setShowImport(true);
                    }}
                    className="flex-1 bg-indigo-50 border border-indigo-200 text-indigo-700 py-1.5 rounded-lg text-[10px] font-semibold flex items-center justify-center gap-1.5 hover:bg-indigo-100 transition-colors shadow-sm"
                    type="button"
                  >
                    <Download className="w-3 h-3" /> IMPORTAR DO CRM
                  </button>
                  <button
                    onClick={addParty}
                    className="bg-white border border-gray-200 text-gray-700 py-1.5 px-2.5 rounded-lg text-[11px] font-bold flex items-center justify-center gap-1.5 hover:bg-gray-50 transition-colors shadow-sm"
                    type="button"
                    title="Adicionar Revogante"
                  >
                    <Plus className="w-3 h-3" />
                  </button>
                </div>

                <div className="space-y-2.5">
                  {partiesList.map((party, idx) => (
                    <div key={party.id} className="bg-white border border-gray-200 rounded-lg p-2 shadow-sm">
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-[9px] font-semibold text-gray-500 uppercase tracking-[0.04em]">
                          {party?.role || "REVOGANTE"} {idx + 1}
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              setImportTargetPartyId(party.id);
                              setShowImport(true);
                            }}
                            className="text-[10px] font-bold text-indigo-700 bg-indigo-50 border border-indigo-200 rounded px-2 py-1 hover:bg-indigo-100"
                            type="button"
                          >
                            Importar CRM
                          </button>
                          <button
                            onClick={() => removeParty(party.id)}
                            className="text-[10px] font-bold text-red-600 bg-white border border-gray-200 rounded px-2 py-1 hover:bg-red-50"
                            type="button"
                            disabled={partiesList.length <= 1}
                            title="Remover Revogante"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 mt-2.5">
                        <div className="space-y-1 col-span-2">
                          <label className={labelBaseStyle}>Nome</label>
                          <input className={inputBaseStyle + " bg-gray-50 text-[10px] font-normal text-gray-500 cursor-not-allowed"} value={toTitleCasePt(resolveClientDisplayName(party?.qualificationData) || party?.name || "")} readOnly disabled />
                        </div>
                        <div className="space-y-1">
                          <label className={labelBaseStyle}>CPF/CNPJ</label>
                          <input className={inputBaseStyle + " bg-gray-50 text-[10px] font-normal text-gray-500 cursor-not-allowed"} value={resolveClientDisplayCpf(party?.qualificationData) || party?.cpf || ""} readOnly disabled />
                        </div>
                        <div className="space-y-1">
                          <label className={labelBaseStyle}>Papel</label>
                          <select className={inputBaseStyle + " bg-gray-50 text-[10px] font-normal text-gray-500 cursor-not-allowed"} value="REVOGANTE" disabled>
                            <option value="REVOGANTE">REVOGANTE</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </ConfigSection>

            <ConfigSection id="processo" title="Processo e Vínculo" icon="fa-link" openSection={openSection} setOpenSection={setOpenSection}>
              <div className="space-y-2.5">
                <div className="space-y-1">
                  <label className={labelBaseStyle}>Processo / Causa (referência)</label>
                  <input
                    className={inputBaseStyle}
                    value={processoRef}
                    onChange={(e) => { setProcessoRef(e.target.value); setIsModified(true); }}
                    placeholder="Ex: Processo n. 0000000-00.0000.8.16.0000"
                  />
                </div>
                <div className="space-y-1">
                  <label className={labelBaseStyle}>Objeto do Mandato Revogado (escopo)</label>
                  <textarea
                    className={inputBaseStyle + " resize-none min-h-[100px]"}
                    value={objetoMandato}
                    onChange={(e) => { setObjetoMandato(e.target.value); setIsModified(true); }}
                    placeholder="Ex: Defesa em ação de cobrança perante a 3ª Vara Cível de Curitiba/PR"
                  />
                </div>
                {clientsList.length > 0 ? (
                  <div className="space-y-1">
                    <CadernoSelector
                      clientId={clientId}
                      clientName={clientName}
                      value={cadernoId}
                      onChange={(id, title) => { setCadernoId(id); setCadernoTitle(title); }}
                    />
                    <p className="text-[9px] text-gray-400 mt-0.5">O Termo será vinculado ao caderno selecionado e aparecerá no portal do cliente.</p>
                  </div>
                ) : (
                  <p className="text-[10px] text-gray-400 italic">Importe um cliente do CRM para vincular a um processo.</p>
                )}
              </div>
            </ConfigSection>
          </div>

        </>
      </DocumentShell>

      {dialogState.open ? (
        <div className="fixed inset-0 z-[700] flex items-center justify-center p-4 no-print">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDialogState({ open: false, title: "", message: "", mode: "confirm", onConfirm: null, onCancel: null })} />
          <div className="relative w-full max-w-md bg-white rounded-xl shadow-2xl border border-gray-200 p-5">
            <div className="text-sm font-bold text-[#20243B]">{dialogState.title || "Confirmar"}</div>
            <div className="text-xs text-gray-600 mt-2 whitespace-pre-wrap">{dialogState.message}</div>
            <div className="mt-5">
              {dialogState.mode === "confirm" ? (
                <SaveCancelBar
                  onCancel={() => { const fn = dialogState.onCancel; setDialogState({ open: false, title: "", message: "", mode: "confirm", onConfirm: null, onCancel: null }); if (typeof fn === "function") fn(); }}
                  onSave={() => { const fn = dialogState.onConfirm; setDialogState({ open: false, title: "", message: "", mode: "confirm", onConfirm: null, onCancel: null }); if (typeof fn === "function") fn(); }}
                  cancelLabel="Cancelar"
                  saveLabel="Confirmar"
                />
              ) : (
                <SaveCancelBar
                  onCancel={() => setDialogState({ open: false, title: "", message: "", mode: "confirm", onConfirm: null, onCancel: null })}
                  onSave={() => setDialogState({ open: false, title: "", message: "", mode: "confirm", onConfirm: null, onCancel: null })}
                  cancelLabel=""
                  saveLabel="OK"
                  disabledCancel={true}
                />
              )}
            </div>
          </div>
        </div>
      ) : null}

      {showImport && createPortal(
        <CrmClientPickerModal isOpen={showImport} onClose={() => setShowImport(false)} onSelect={handleImportClient} db={db} appId={appId} title="Base de Clientes (CRM) — Parte REVOGANTE" />,
        document.body,
      )}

      {toastMsg && (
        <div className="fixed bottom-6 right-6 z-[1400] bg-[#20243B] text-white text-xs font-bold px-4 py-2.5 rounded-xl shadow-2xl border border-[#C5A059]/30 flex items-center gap-2 no-print">
          <CheckCircle className="w-4 h-4 text-[#C5A059]" />
          {toastMsg}
        </div>
      )}
    </>
  );
}

export default TermoRevogacaoClienteFactory;
